<template>
  <div class="asset-grid flex flex-col h-full">
    <!-- 网格头部 -->
    <div class="grid-header px-4 py-2.5 border-b border-slate-100/80 flex items-center justify-between shrink-0 bg-white">
      <span class="text-sm text-slate-500">
        共 <span class="font-semibold text-slate-700">{{ total }}</span> 个素材
        <span v-if="selectedId" class="ml-2 text-primary-600 cursor-pointer hover:text-primary-700 transition-colors text-xs" @click="$emit('select', null)">
          清除选择
        </span>
      </span>
      <div class="flex items-center gap-0.5">
        <el-button
          v-for="s in sortOptions"
          :key="s.field"
          text
          size="small"
          :type="sortField === s.field ? 'primary' : 'default'"
          class="!text-xs !px-2"
          @click="toggleSort(s.field)"
        >
          {{ s.label }}
          <el-icon v-if="sortField === s.field" :size="12" class="ml-0.5 transition-transform">
            <ArrowUp v-if="sortOrder === 'ASC'" />
            <ArrowDown v-else />
          </el-icon>
        </el-button>
      </div>
    </div>

    <!-- 网格内容 -->
    <div class="grid-body flex-1 overflow-y-auto p-4" v-loading="loading">
      <!-- 加载骨架 -->
      <div v-if="loading && list.length === 0" class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
        <div v-for="i in 10" :key="i" class="bg-white rounded-xl border border-slate-200/60 p-3 animate-pulse">
          <div class="aspect-[4/3] bg-gradient-to-br from-slate-100 to-slate-50 rounded-lg mb-3"></div>
          <div class="h-3 bg-slate-100 rounded w-3/4 mb-2"></div>
          <div class="h-2.5 bg-slate-100 rounded w-1/2"></div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-else-if="!loading && list.length === 0" class="flex flex-col items-center justify-center py-20">
        <div class="w-20 h-20 rounded-full bg-slate-100/80 flex items-center justify-center mb-4">
          <el-icon :size="36" class="text-slate-300"><FolderOpened /></el-icon>
        </div>
        <p class="text-slate-500 text-sm font-medium mb-1">暂无素材</p>
        <p class="text-slate-400 text-xs">选择文件夹后上传或新建素材</p>
      </div>

      <!-- 卡片网格 -->
      <div v-else class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
        <div
          v-for="item in list"
          :key="item.id"
          class="asset-card bg-white rounded-xl border border-slate-200/80 cursor-pointer transition-all duration-200 overflow-hidden group hover:shadow-lg hover:shadow-slate-200/50 hover:border-slate-300/80 hover:-translate-y-0.5"
          :class="selectedId === item.id ? 'border-primary-400 ring-2 ring-primary-100 shadow-lg shadow-primary-100/50' : ''"
          @click="$emit('select', item)"
        >
          <!-- 缩略图 -->
          <div class="aspect-[4/3] bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center relative overflow-hidden">
            <!-- 图片 -->
            <div v-if="item.type === 'IMAGE'" class="w-full h-full flex items-center justify-center text-slate-300">
              <img
                v-if="item.coverUrl"
                :src="item.coverUrl"
                :alt="item.name"
                class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
              <el-icon v-else :size="36"><Picture /></el-icon>
            </div>
            <!-- 视频 -->
            <div v-else-if="item.type === 'VIDEO'" class="w-full h-full relative">
              <img v-if="item.coverUrl" :src="item.coverUrl" :alt="item.name" class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
              <div class="absolute inset-0 bg-black/20 flex items-center justify-center">
                <div class="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                  <el-icon :size="20" :class="item.coverUrl ? 'text-slate-700' : 'text-slate-400'"><VideoPlay /></el-icon>
                </div>
              </div>
            </div>
            <!-- 文件 -->
            <div v-else-if="item.type === 'FILE'" class="flex flex-col items-center text-slate-400">
              <div class="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center mb-1">
                <el-icon :size="32"><Document /></el-icon>
              </div>
              <span class="text-[10px] mt-1 text-slate-400 font-mono uppercase tracking-wider">{{ fileExtension(item) }}</span>
            </div>
            <!-- 文本 -->
            <div v-else-if="item.type === 'TEXT'" class="flex flex-col items-center text-slate-400 px-4 py-2">
              <div class="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mb-1">
                <el-icon :size="24"><Tickets /></el-icon>
              </div>
              <span class="text-[10px] text-slate-400 text-center line-clamp-2">{{ item.description || '文本素材' }}</span>
            </div>
            <!-- hover 遮罩 -->
            <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200 flex items-center justify-center">
              <el-icon v-if="item.type === 'IMAGE'" :size="24" class="text-white opacity-0 group-hover:opacity-100 transition-all duration-200 transform scale-75 group-hover:scale-100 drop-shadow-lg"><ZoomIn /></el-icon>
            </div>
          </div>
          <!-- 卡片信息 -->
          <div class="p-3">
            <div class="flex items-center gap-1.5 mb-2">
              <span class="text-[10px] font-medium px-1.5 py-0.5 rounded-md" :class="typeBadgeClass(item.type)">
                {{ typeLabel(item.type) }}
              </span>
              <span v-if="item.status === 'DISABLED'" class="text-[10px] px-1.5 py-0.5 rounded-md bg-red-50 text-red-600 font-medium">已禁用</span>
            </div>
            <p class="text-sm font-semibold text-slate-800 truncate mb-0.5" :title="item.name">{{ item.name }}</p>
            <p class="text-[11px] text-slate-400">{{ item.createBy }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 分页 -->
    <div v-if="total > 0" class="grid-footer px-4 py-2.5 border-t border-slate-100/80 flex items-center justify-between shrink-0 bg-white">
      <el-pagination
        :current-page="current"
        :page-size="size"
        :total="total"
        :page-sizes="[20, 40, 60, 100]"
        layout="total, sizes, prev, pager, next"
        small
        background
        class="!text-xs"
        @current-change="$emit('page-change', $event)"
        @size-change="$emit('size-change', $event)"
      />
    </div>
  </div>
</template>

<script setup>
import { FolderOpened, VideoPlay, Document, Tickets, ZoomIn, ArrowUp, ArrowDown, Picture } from '@element-plus/icons-vue'

const props = defineProps({
  list: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  selectedId: { type: [Number, String], default: null },
  total: { type: Number, default: 0 },
  current: { type: Number, default: 1 },
  size: { type: Number, default: 20 },
  sortField: { type: String, default: 'createTime' },
  sortOrder: { type: String, default: 'DESC' }
})

const emit = defineEmits(['select', 'page-change', 'size-change', 'sort-change'])

const sortOptions = [
  { field: 'createTime', label: '时间' },
  { field: 'name', label: '名称' },
  { field: 'fileSize', label: '大小' }
]

function toggleSort(field) {
  const nextOrder = props.sortField === field && props.sortOrder === 'ASC' ? 'DESC' : 'ASC'
  emit('sort-change', { field, order: nextOrder })
}

function typeLabel(type) {
  const map = { IMAGE: '图片', VIDEO: '视频', FILE: '文件', TEXT: '文本' }
  return map[type] || type
}

function typeBadgeClass(type) {
  const map = {
    IMAGE: 'bg-emerald-50 text-emerald-700',
    VIDEO: 'bg-violet-50 text-violet-700',
    FILE: 'bg-sky-50 text-sky-700',
    TEXT: 'bg-amber-50 text-amber-700'
  }
  return map[type] || ''
}

function fileExtension(item) {
  // Try to extract from cover info or name
  if (item.name?.includes('.')) {
    const extension = item.name.split('.').pop()
    return extension ? extension.substring(0, 4) : 'FILE'
  }
  return 'FILE'
}
</script>
